import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { api } from '@/shared/api/client'
import type { ProjectRead } from '@/shared/api/contracts'
import { qk } from '@/shared/api/query-keys'
import { errorMessage } from '@/shared/components/data'
import { useT } from '@/shared/i18n'
import type { ProjectSummary, Stage, UserSummary } from '@/shared/types/domain'

import { patchProjectLists } from './reorder'

/** Lo que se cambia desde una fila del portafolio. Un campo por vez. */
export type ProjectRowPatch =
  | { stage: Stage }
  | { internal_owner_id: string | null; internal_owner: UserSummary | null }
  | { start_date: string | null }
  | { target_end_date: string | null }

export interface PatchProjectRowVariables {
  id: string
  patch: ProjectRowPatch
}

/**
 * Editar un proyecto desde la tabla del portafolio: fase, responsable y fechas.
 *
 * Aparte de `useUpdateProject`, que está atado a UN proyecto (el de la ficha) y
 * pinta el detalle; acá el id viaja con cada cambio y lo que se pinta son las
 * listas del portafolio — todas, igual que al reordenar, para que la tabla, el
 * tablero y la línea de tiempo no se contradigan.
 *
 * Mismos endpoints que la ficha, por la misma razón: la fase va por
 * `POST /projects/{id}/move` (el `PATCH` no la acepta) y el resto por
 * `PATCH /projects/{id}`. Optimista, porque es un clic en una fila.
 */
export function usePatchProjectRow() {
  const client = useQueryClient()
  const t = useT()

  return useMutation({
    mutationFn: ({ id, patch }: PatchProjectRowVariables): Promise<ProjectRead> => {
      if ('stage' in patch) {
        return api.post<ProjectRead>(`projects/${id}/move`, { stage_id: patch.stage.id })
      }
      if ('internal_owner_id' in patch) {
        return api.patch<ProjectRead>(`projects/${id}`, {
          internal_owner_id: patch.internal_owner_id,
        })
      }
      return api.patch<ProjectRead>(`projects/${id}`, { ...patch })
    },
    onMutate: async ({ id, patch }) => {
      await client.cancelQueries({ queryKey: qk.portfolio.all })
      const previous = client.getQueriesData({ queryKey: qk.portfolio.all })
      patchProjectLists(client, id, (projects) =>
        projects.map((project): ProjectSummary =>
          project.id === id ? { ...project, ...patch } : project,
        ),
      )
      return { previous }
    },
    onError: (error, _variables, context) => {
      for (const [key, data] of context?.previous ?? []) client.setQueryData(key, data)
      toast.error(errorMessage(error, t('projects.error.save')))
    },
    onSettled: (_data, _error, { id }) => {
      void client.invalidateQueries({ queryKey: qk.portfolio.all })
      void client.invalidateQueries({ queryKey: qk.projects.detail(id) })
      void client.invalidateQueries({ queryKey: qk.projects.lists() })
    },
  })
}
